import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type {
  MemberGroupDetail,
  MemberGroupMemberListItem,
  MemberGroupSummary,
  MemberGroupTagListItem,
  MemberSelectorListItem,
  PermissionGroupSummary
} from '../appService';
import { formatPermissionKeyLabels } from '../permissions';
import type { WorkspaceBootstrapContext } from './appTypes';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  invalidateMemberGroupCreateQueries,
  invalidateMemberGroupManagerGrantQueries,
  invalidateMemberGroupMembershipMutationQueries,
  invalidateMemberGroupStructureQueries,
  invalidateMemberGroupTagMutationQueries,
  useMemberGroupDetailQuery,
  useMemberGroupSummaryListQuery,
  useMemberSelectorItemsQuery,
  usePermissionGroupListQuery
} from './data';
import { useMemberGroupCreateDraftStore, type MemberGroupCreateDraftStoreValue } from './draftStores';
import {
  ActionError,
  BackHeader,
  CompactEmptyRow,
  ConfirmedField,
  DiscardUnsavedChangesDialog,
  ModuleEmptyState,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  TextField,
  formatActionError,
  formatMemberGroupNames
} from './ui';

export type MemberGroupDetailTab = 'members' | 'permissions' | 'tags';

const MEMBER_GROUP_DETAIL_TABS: readonly MemberGroupDetailTab[] = ['members', 'permissions', 'tags'];

export function isMemberGroupDetailTab(value: unknown): value is MemberGroupDetailTab {
  return typeof value === 'string' && MEMBER_GROUP_DETAIL_TABS.includes(value as MemberGroupDetailTab);
}

function MemberGroupCreateLeaveGuard({
  isDirty,
  isDisabled = false,
  onDiscard
}: {
  readonly isDirty: boolean;
  readonly isDisabled?: boolean;
  readonly onDiscard: () => void;
}): ReactNode {
  const blocker = useMemberGroupCreateLeaveBlocker({ isDirty, isDisabled });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <DiscardUnsavedChangesDialog
      body="离开后，当前成员组草稿不会保存。"
      onKeepEditing={blocker.reset}
      onDiscard={() => {
        onDiscard();
        blocker.proceed();
      }}
    />
  );
}

function MemberGroupRenameLeaveGuard({
  memberGroupId,
  isDirty,
  isDisabled
}: {
  readonly memberGroupId: string;
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}): ReactNode {
  const blocker = useMemberGroupRenameLeaveBlocker({ memberGroupId, isDirty, isDisabled });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <DiscardUnsavedChangesDialog
      body="离开后，当前成员组更改不会保存。"
      onKeepEditing={blocker.reset}
      onDiscard={blocker.proceed}
    />
  );
}

function MemberGroupTagFormLeaveGuard({
  memberGroupId,
  isDirty,
  isDisabled
}: {
  readonly memberGroupId: string;
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}): ReactNode {
  const blocker = useMemberGroupTagFormLeaveBlocker({ memberGroupId, isDirty, isDisabled });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <DiscardUnsavedChangesDialog
      body="离开后，当前标签更改不会保存。"
      onKeepEditing={blocker.reset}
      onDiscard={blocker.proceed}
    />
  );
}

export function MemberSettingsPage(): ReactNode {
  const navigate = useNavigate();
  const routeAdapter = useWorkspaceRouteAdapter();
  const appContext = useWorkspaceBootstrapContext();
  const canManagePermissions = canManagePermissionGroups(appContext);
  const memberGroups = useMemberGroupSummaryListQuery();
  const permissionGroups = usePermissionGroupListQuery(canManagePermissions);

  return (
    <div className="route-outlet">
      <BackHeader
        title="成员设置"
        action="back-to-choir-management"
        label="返回合唱团管理"
        onBack={() => {
          void navigate({ to: '/workspace/manage/choir', replace: true, resetScroll: true });
        }}
      />
      <section className="schedule-detail-section">
        <h2>成员组</h2>
        <button
          type="button"
          className="secondary-button"
          data-action="open-member-groups"
          onClick={routeAdapter.openMemberGroups}
        >
          管理分组
        </button>
      </section>
      <MemberGroupSummaryListContent
        memberGroups={memberGroups}
        loadingBody="正在读取成员组。"
        emptyBody="暂无成员组"
        retryAction="retry-member-settings-groups"
      />
      {canManagePermissions ? (
        <>
          <section className="schedule-detail-section">
            <h2>权限组</h2>
            <button
              type="button"
              className="secondary-button"
              data-action="open-permission-groups"
              onClick={routeAdapter.openPermissionGroups}
            >
              管理权限组
            </button>
          </section>
          <PermissionGroupSettingsSummaryContent permissionGroups={permissionGroups} />
        </>
      ) : null}
    </div>
  );
}

function PermissionGroupSettingsSummaryContent({
  permissionGroups
}: {
  readonly permissionGroups: ReturnType<typeof usePermissionGroupListQuery>;
}): ReactNode {
  if (permissionGroups.status === 'pending') {
    return <SectionLoading body="正在读取权限组。" />;
  }

  if (permissionGroups.status === 'error') {
    return (
      <RecoverableReadError
        error={permissionGroups.error}
        retryAction="retry-member-settings-permission-groups"
        onRetry={() => void permissionGroups.refetch()}
      />
    );
  }

  if (permissionGroups.data.length === 0) {
    return <ModuleEmptyState icon="shield" body="暂无权限组" />;
  }

  return (
    <section className="permission-group-list prototype-list" role="list">
      {permissionGroups.data.map((permissionGroup) => (
        <article
          className="permission-group-row prototype-list-row"
          role="listitem"
          key={permissionGroup.permissionGroupId}
        >
          <span className="member-avatar member-avatar-icon" aria-hidden="true">
            <SvgIcon name="shield" className="member-avatar-svg" />
          </span>
          <span className="member-copy">
            <strong>{permissionGroup.name}</strong>
            <small>{formatPermissionGroupSummaryMeta(permissionGroup)}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

export function CreateMemberGroupPage(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const draftStore = useMemberGroupCreateDraftStore();
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const createMemberGroup = useMutation({
    mutationFn: (input: { readonly name: string; readonly initialSeasonMembershipId?: string }) => {
      const createInput = input.initialSeasonMembershipId
        ? {
            workspaceContext,
            name: input.name,
            initialSeasonMembershipId: input.initialSeasonMembershipId
          }
        : {
            workspaceContext,
            name: input.name
          };
      return service.createMemberGroup(createInput);
    },
    onSuccess: async (createdMemberGroup, input) => {
      await invalidateMemberGroupCreateQueries(queryClient, workspaceContext, {
        memberGroupId: createdMemberGroup.memberGroupId,
        affectsMembership: Boolean(input.initialSeasonMembershipId)
      });
      flushSync(() => {
        setSubmitNavigationAllowed(true);
        draftStore.clearDraft();
      });
      void navigate({
        to: '/workspace/member-groups/$memberGroupId',
        params: { memberGroupId: createdMemberGroup.memberGroupId },
        search: { tab: 'members' },
        replace: true,
        resetScroll: true
      });
    }
  });
  const isDirty = isMemberGroupCreateDraftDirty(draftStore);
  const leave = (): void => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    draftStore.clearDraft();
    void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
  };

  if (!canCreateMemberGroups(appContext)) {
    return (
      <MemberGroupPermissionDenied
        title="新建成员组"
        action="back-to-member-groups"
        label="返回成员组"
        onBack={() => {
          void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <BackHeader
        title="新建成员组"
        action="back-to-member-groups"
        label="返回成员组"
        pending={createMemberGroup.isPending}
        onBack={leave}
      />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          body="离开后，当前成员组草稿不会保存。"
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={() => {
            setShowDiscardDialog(false);
            setSubmitNavigationAllowed(true);
            draftStore.clearDraft();
            void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
          }}
        />
      ) : null}
      <MemberGroupCreateLeaveGuard
        isDirty={isDirty}
        isDisabled={isSubmitNavigationAllowed}
        onDiscard={draftStore.clearDraft}
      />
      <section className="member-group-form">
        <TextField
          label="成员组名称"
          field="member-group-name"
          value={draftStore.name}
          onChange={(name) => {
            setActionError(null);
            draftStore.setName(name);
          }}
        />
        <MemberGroupInitialMemberSummary
          member={draftStore.initialMember}
          onChoose={() => {
            void navigate({
              to: '/workspace/member-groups/new/initial-member',
              resetScroll: true
            });
          }}
        />
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton
          action="save-member-group"
          label="保存"
          variant="primary"
          pending={createMemberGroup.isPending}
          onClick={() => {
            void (async () => {
              setActionError(null);
              try {
                const initialSeasonMembershipId = draftStore.initialMember?.seasonMembershipId;
                await createMemberGroup.mutateAsync(
                  initialSeasonMembershipId
                    ? { name: draftStore.name, initialSeasonMembershipId }
                    : { name: draftStore.name }
                );
              } catch (error) {
                setSubmitNavigationAllowed(false);
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
      </div>
    </div>
  );
}

function MemberGroupInitialMemberSummary({
  member,
  onChoose
}: {
  readonly member: MemberSelectorListItem | null;
  readonly onChoose: () => void;
}): ReactNode {
  return (
    <section className="schedule-detail-section">
      <div className="section-heading-row">
        <h2>第一个成员</h2>
        <button type="button" className="text-button" data-action="choose-initial-member" onClick={onChoose}>
          选择第一个成员
        </button>
      </div>
      {member ? (
        <ConfirmedField
          label="已选择"
          value={`${member.nickname} · ${formatMemberGroupNames(member.memberGroupNames)}`}
        />
      ) : (
        <p className="empty-hint">可先创建空成员组，稍后再维护成员。</p>
      )}
    </section>
  );
}

export function CreateMemberGroupInitialMemberPage(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const navigate = useNavigate();
  const draftStore = useMemberGroupCreateDraftStore();
  const canCreateGroups = canCreateMemberGroups(appContext);
  const members = useMemberSelectorItemsQuery('member_group_initial_member', canCreateGroups);

  if (!canCreateGroups) {
    return (
      <MemberGroupPermissionDenied
        title="选择第一个成员"
        action="back-to-member-group-create"
        label="返回新建成员组"
        onBack={() => {
          void navigate({ to: '/workspace/member-groups/new', replace: true, resetScroll: true });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <BackHeader
        title="选择第一个成员"
        action="back-to-member-group-create"
        label="返回新建成员组"
        onBack={() => {
          void navigate({ to: '/workspace/member-groups/new', replace: true, resetScroll: true });
        }}
      />
      <MemberGroupCreateLeaveGuard
        isDirty={isMemberGroupCreateDraftDirty(draftStore)}
        onDiscard={draftStore.clearDraft}
      />
      <MemberInitialMemberSelectorContent
        members={members}
        onSelect={(member) => {
          draftStore.setInitialMember(member);
          void navigate({ to: '/workspace/member-groups/new', replace: true, resetScroll: true });
        }}
      />
    </div>
  );
}

function MemberInitialMemberSelectorContent({
  members,
  onSelect
}: {
  readonly members: ReturnType<typeof useMemberSelectorItemsQuery>;
  readonly onSelect: (member: MemberSelectorListItem) => void;
}): ReactNode {
  if (members.status === 'pending') {
    return <SectionLoading body="正在读取成员选择列表。" />;
  }

  if (members.status === 'error') {
    return (
      <RecoverableReadError
        error={members.error}
        retryAction="retry-member-group-initial-member-selector"
        onRetry={() => void members.refetch()}
      />
    );
  }

  if (members.data.length === 0) {
    return <CompactEmptyRow body="暂无可选择成员" />;
  }

  return (
    <section className="member-selector-list prototype-list" role="list">
      {members.data.map((member) => (
        <button
          type="button"
          className="member-selector-row prototype-list-row"
          data-action="select-initial-member"
          data-season-membership-id={member.seasonMembershipId}
          role="listitem"
          key={member.seasonMembershipId}
          onClick={() => onSelect(member)}
        >
          <span className="member-avatar member-avatar-icon" aria-hidden="true">
            <SvgIcon name="users" className="member-avatar-svg" />
          </span>
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatMemberGroupNames(member.memberGroupNames)}</small>
          </span>
          <span className="choir-context-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </section>
  );
}

export function MemberGroupsPage(): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const appContext = useWorkspaceBootstrapContext();
  const memberGroups = useMemberGroupSummaryListQuery();
  const canCreateGroups = canCreateMemberGroups(appContext);
  const canDeleteGroups = canDeleteMemberGroups(appContext);

  return (
    <div className="route-outlet">
      <BackHeader
        title="成员组"
        action="back-to-member-settings"
        label="返回成员设置"
        onBack={routeAdapter.backToMemberSettings}
      />
      {canCreateGroups ? (
        <section className="schedule-detail-section">
          <div className="section-heading-row">
            <h2>当前成员组</h2>
            <button
              type="button"
              className="text-button"
              data-action="open-create-member-group"
              onClick={() => {
                routeAdapter.openCreateMemberGroup();
              }}
            >
              新建
            </button>
          </div>
        </section>
      ) : null}
      <MemberGroupSummaryListContent
        memberGroups={memberGroups}
        loadingBody="正在读取成员组。"
        emptyBody="暂无成员组"
        retryAction="retry-member-group-summary-list"
        showActions
        canDeleteGroups={canDeleteGroups}
      />
    </div>
  );
}

function MemberGroupSummaryListContent({
  memberGroups,
  loadingBody,
  emptyBody,
  retryAction,
  showActions = false,
  canDeleteGroups = false
}: {
  readonly memberGroups: ReturnType<typeof useMemberGroupSummaryListQuery>;
  readonly loadingBody: string;
  readonly emptyBody: string;
  readonly retryAction: string;
  readonly showActions?: boolean;
  readonly canDeleteGroups?: boolean;
}): ReactNode {
  if (memberGroups.status === 'pending') {
    return <SectionLoading body={loadingBody} />;
  }

  if (memberGroups.status === 'error') {
    return (
      <RecoverableReadError
        error={memberGroups.error}
        retryAction={retryAction}
        onRetry={() => void memberGroups.refetch()}
      />
    );
  }

  if (memberGroups.data.length === 0) {
    return <ModuleEmptyState icon="users" body={emptyBody} />;
  }

  return (
    <MemberGroupSummaryRows
      memberGroups={memberGroups.data}
      showActions={showActions}
      canDeleteGroups={canDeleteGroups}
    />
  );
}

function MemberGroupSummaryRows({
  memberGroups,
  showActions,
  canDeleteGroups
}: {
  readonly memberGroups: readonly MemberGroupSummary[];
  readonly showActions: boolean;
  readonly canDeleteGroups: boolean;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <section className="member-group-list prototype-list" role="list">
      {memberGroups.map((memberGroup) => {
        const canRename = canRenameMemberGroup(memberGroup);
        const canDelete = canDeleteMemberGroup(canDeleteGroups, memberGroup);

        return (
          <article
            className="member-group-row prototype-list-row"
            role="listitem"
            data-member-group-id={memberGroup.memberGroupId}
            key={memberGroup.memberGroupId}
          >
            <span className="member-avatar member-avatar-icon" aria-hidden="true">
              <SvgIcon name="users" className="member-avatar-svg" />
            </span>
            <span className="member-copy">
              <strong>{memberGroup.name}</strong>
              <small>{formatMemberGroupSummaryMeta(memberGroup)}</small>
            </span>
            {showActions ? (
              <span className="member-group-row-actions">
                <button
                  type="button"
                  className="text-button"
                  data-action="open-member-group-detail"
                  data-member-group-id={memberGroup.memberGroupId}
                  onClick={() => {
                    routeAdapter.openMemberGroupDetail(memberGroup.memberGroupId);
                  }}
                >
                  详情
                </button>
                {canRename ? (
                  <button
                    type="button"
                    className="text-button"
                    data-action="rename-member-group"
                    data-member-group-id={memberGroup.memberGroupId}
                    onClick={() => {
                      routeAdapter.openRenameMemberGroup(memberGroup.memberGroupId);
                    }}
                  >
                    重命名
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    className="text-button danger-text-button"
                    data-action="delete-member-group"
                    data-member-group-id={memberGroup.memberGroupId}
                    onClick={() => {
                      routeAdapter.openDeleteMemberGroup(memberGroup.memberGroupId);
                    }}
                  >
                    删除
                  </button>
                ) : null}
              </span>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}

export function MemberGroupDetailPage({
  memberGroupId,
  tab
}: {
  readonly memberGroupId: string;
  readonly tab: MemberGroupDetailTab;
}): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);

  return (
    <div className="route-outlet">
      <BackHeader
        title="成员组详情"
        action="back-to-member-groups"
        label="返回成员组"
        onBack={() => {
          void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
        }}
      />
      <MemberGroupDetailContent
        detail={detail}
        tab={tab}
        canDeleteGroups={canDeleteMemberGroups(appContext)}
        onTabChange={(nextTab) => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId',
            params: { memberGroupId },
            search: { tab: nextTab },
            replace: true,
            resetScroll: false
          });
        }}
      />
    </div>
  );
}

function MemberGroupDetailContent({
  detail,
  tab,
  canDeleteGroups,
  onTabChange
}: {
  readonly detail: ReturnType<typeof useMemberGroupDetailQuery>;
  readonly tab: MemberGroupDetailTab;
  readonly canDeleteGroups: boolean;
  readonly onTabChange: (tab: MemberGroupDetailTab) => void;
}): ReactNode {
  const navigate = useNavigate();

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取成员组详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-member-group-detail"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const memberGroup = detail.data;
  const canRename = canRenameMemberGroup(memberGroup);
  const canDelete = canDeleteMemberGroup(canDeleteGroups, memberGroup);
  const canShowManageActions =
    tab === 'tags' ? memberGroup.canManage : memberGroup.canManage || memberGroup.canAddMembers;

  return (
    <>
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>{memberGroup.name}</h2>
          {canRename || canDelete ? (
            <span className="member-group-row-actions">
              {canRename ? (
                <button
                  type="button"
                  className="text-button"
                  data-action="rename-member-group"
                  onClick={() => {
                    void navigate({
                      to: '/workspace/member-groups/$memberGroupId/rename',
                      params: { memberGroupId: memberGroup.memberGroupId },
                      resetScroll: true
                    });
                  }}
                >
                  重命名
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  className="text-button danger-text-button"
                  data-action="delete-member-group"
                  onClick={() => {
                    void navigate({
                      to: '/workspace/member-groups/$memberGroupId/delete',
                      params: { memberGroupId: memberGroup.memberGroupId },
                      resetScroll: true
                    });
                  }}
                >
                  删除
                </button>
              ) : null}
            </span>
          ) : null}
        </div>
        <ConfirmedField label="类型" value={memberGroup.isDefault ? '默认分组' : '自定义分组'} />
        <ConfirmedField label="成员" value={`${memberGroup.memberCount} 人`} />
        <ConfirmedField label="维护权限" value={memberGroup.canManage ? '可管理' : '仅查看'} />
      </section>
      <MemberGroupDetailTabs activeTab={tab} onTabChange={onTabChange} />
      {tab === 'members' ? <MemberGroupMembersStatus members={memberGroup.members} /> : null}
      {tab === 'permissions' ? <MemberGroupPermissionStatus members={memberGroup.members} /> : null}
      {tab === 'tags' ? <MemberGroupTagsStatus tags={memberGroup.tags} /> : null}
      {canShowManageActions ? <MemberGroupManageActions memberGroup={memberGroup} activeTab={tab} /> : null}
    </>
  );
}

function MemberGroupManageActions({
  memberGroup,
  activeTab
}: {
  readonly memberGroup: MemberGroupDetail;
  readonly activeTab: MemberGroupDetailTab;
}): ReactNode {
  const navigate = useNavigate();

  if (activeTab === 'members' || activeTab === 'permissions') {
    return (
      <div className="footer-actions">
        <button
          type="button"
          className="secondary-button"
          data-action="open-manage-member-group-members"
          onClick={() => {
            void navigate({
              to: '/workspace/member-groups/$memberGroupId/members',
              params: { memberGroupId: memberGroup.memberGroupId },
              resetScroll: true
            });
          }}
        >
          管理成员
        </button>
      </div>
    );
  }

  return (
    <div className="footer-actions">
      <button
        type="button"
        className="secondary-button"
        data-action="open-manage-member-group-tags"
        onClick={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags',
            params: { memberGroupId: memberGroup.memberGroupId },
            resetScroll: true
          });
        }}
      >
        管理标签
      </button>
    </div>
  );
}

function MemberGroupDetailTabs({
  activeTab,
  onTabChange
}: {
  readonly activeTab: MemberGroupDetailTab;
  readonly onTabChange: (tab: MemberGroupDetailTab) => void;
}): ReactNode {
  const tabs: readonly {
    readonly tab: MemberGroupDetailTab;
    readonly label: string;
    readonly action: string;
  }[] = [
    { tab: 'members', label: '成员', action: 'show-member-group-members' },
    { tab: 'permissions', label: '管理权限', action: 'show-member-group-permissions' },
    { tab: 'tags', label: '标签', action: 'show-member-group-tags' }
  ];

  return (
    <section className="member-group-detail-tabs" role="tablist" aria-label="成员组详情页签">
      {tabs.map((item) => (
        <button
          type="button"
          className="member-group-detail-tab text-button"
          data-action={item.action}
          role="tab"
          aria-selected={activeTab === item.tab}
          key={item.tab}
          onClick={() => onTabChange(item.tab)}
        >
          {item.label}
        </button>
      ))}
    </section>
  );
}

function MemberGroupMembersStatus({ members }: { readonly members: readonly MemberGroupMemberListItem[] }): ReactNode {
  if (members.length === 0) {
    return <CompactEmptyRow body="暂无成员" />;
  }

  return (
    <section className="member-group-detail-list prototype-list" role="list">
      {members.map((member) => (
        <article
          className="member-group-detail-member-row prototype-list-row"
          role="listitem"
          key={member.seasonMembershipId}
        >
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{[formatMemberRole(member.role), formatMemberGroupMemberTags(member.tagNames)].join(' · ')}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

function MemberGroupPermissionStatus({
  members
}: {
  readonly members: readonly MemberGroupMemberListItem[];
}): ReactNode {
  if (members.length === 0) {
    return <CompactEmptyRow body="暂无成员权限状态" />;
  }

  return (
    <section className="member-group-permission-list prototype-list" role="list">
      {members.map((member) => (
        <article
          className="member-group-permission-row prototype-list-row"
          role="listitem"
          key={member.seasonMembershipId}
        >
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatMemberGroupPermissionStatus(member)}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

function MemberGroupTagsStatus({ tags }: { readonly tags: readonly MemberGroupTagListItem[] }): ReactNode {
  if (tags.length === 0) {
    return <CompactEmptyRow body="暂无标签" />;
  }

  return (
    <section className="member-group-tag-status-list prototype-list" role="list">
      {tags.map((tag) => (
        <article className="member-group-tag-status-row prototype-list-row" role="listitem" key={tag.memberGroupTagId}>
          <span className="member-copy">
            <strong>{tag.name}</strong>
            <small>{`${tag.memberCount} 人`}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

export function RenameMemberGroupPage({ memberGroupId }: { readonly memberGroupId: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const renameMemberGroup = useMutation({
    mutationFn: (name: string) => service.renameMemberGroup({ workspaceContext, memberGroupId, name }),
    onSuccess: async () => {
      await invalidateMemberGroupStructureQueries(queryClient, workspaceContext, memberGroupId);
      flushSync(() => setSubmitNavigationAllowed(true));
      void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
    }
  });

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupDetailBackHeader
          title="重命名成员组"
          memberGroupId={memberGroupId}
          pending={renameMemberGroup.isPending}
        />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupDetailBackHeader
          title="重命名成员组"
          memberGroupId={memberGroupId}
          pending={renameMemberGroup.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-rename-member-group"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  if (!canMutateMemberGroup(detail.data)) {
    return (
      <MemberGroupPermissionDenied
        title="重命名成员组"
        action="back-to-member-group-detail"
        label="返回成员组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  return (
    <MemberGroupNameFormRoute
      key={detail.data.memberGroupId}
      title="重命名成员组"
      initialName={detail.data.name}
      savePending={renameMemberGroup.isPending}
      isSubmitNavigationAllowed={isSubmitNavigationAllowed}
      memberGroupId={memberGroupId}
      onSubmit={async (name) => {
        try {
          await renameMemberGroup.mutateAsync(name);
        } catch (error) {
          setSubmitNavigationAllowed(false);
          throw error;
        }
      }}
    />
  );
}

function MemberGroupNameFormRoute({
  title,
  initialName,
  savePending,
  isSubmitNavigationAllowed,
  memberGroupId,
  onSubmit
}: {
  readonly title: string;
  readonly initialName: string;
  readonly savePending: boolean;
  readonly isSubmitNavigationAllowed: boolean;
  readonly memberGroupId: string;
  readonly onSubmit: (name: string) => Promise<void>;
}): ReactNode {
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isDiscardNavigationAllowed, setDiscardNavigationAllowed] = useState(false);
  const isDirty = name !== initialName;
  const leave = (): void => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    void navigate({
      to: '/workspace/member-groups/$memberGroupId',
      params: { memberGroupId },
      replace: true,
      resetScroll: true
    });
  };

  return (
    <div className="route-outlet">
      <MemberGroupDetailBackHeader title={title} memberGroupId={memberGroupId} pending={savePending} onBack={leave} />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          body="离开后，当前成员组更改不会保存。"
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={() => {
            setShowDiscardDialog(false);
            setDiscardNavigationAllowed(true);
            void navigate({
              to: '/workspace/member-groups/$memberGroupId',
              params: { memberGroupId },
              replace: true,
              resetScroll: true
            });
          }}
        />
      ) : null}
      <MemberGroupRenameLeaveGuard
        memberGroupId={memberGroupId}
        isDirty={isDirty}
        isDisabled={isSubmitNavigationAllowed || isDiscardNavigationAllowed}
      />
      <section className="member-group-form">
        <TextField
          label="成员组名称"
          field="member-group-name"
          value={name}
          onChange={(nextName) => {
            setActionError(null);
            setName(nextName);
          }}
        />
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton
          action="save-member-group"
          label="保存"
          variant="primary"
          pending={savePending}
          onClick={() => {
            void (async () => {
              setActionError(null);
              try {
                await onSubmit(name);
              } catch (error) {
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
      </div>
    </div>
  );
}

export function DeleteMemberGroupPage({ memberGroupId }: { readonly memberGroupId: string }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [actionError, setActionError] = useState<string | null>(null);
  const deleteMemberGroup = useMutation({
    mutationFn: () => service.deleteMemberGroup({ workspaceContext, memberGroupId }),
    onSuccess: async () => {
      await invalidateMemberGroupStructureQueries(queryClient, workspaceContext, memberGroupId);
      void navigate({ to: '/workspace/member-groups', replace: true, resetScroll: true });
    }
  });

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupDetailBackHeader
          title="删除成员组"
          memberGroupId={memberGroupId}
          pending={deleteMemberGroup.isPending}
        />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupDetailBackHeader
          title="删除成员组"
          memberGroupId={memberGroupId}
          pending={deleteMemberGroup.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-confirm-delete-member-group"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const deleteBlockReason = getMemberGroupDeleteBlockReason(appContext, detail.data);
  if (deleteBlockReason) {
    return (
      <MemberGroupPermissionDenied
        title="删除成员组"
        body={deleteBlockReason}
        action="back-to-member-group-detail"
        label="返回成员组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <MemberGroupDetailBackHeader
        title="删除成员组"
        memberGroupId={memberGroupId}
        pending={deleteMemberGroup.isPending}
      />
      <section className="confirm-card">
        <h2>{`删除“${detail.data.name}”？`}</h2>
        <p>删除后，这个成员组会从成员组列表中移除；成员本身不会被删除。</p>
        <ActionError message={actionError} />
        <div className="footer-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="cancel-delete-member-group"
            disabled={deleteMemberGroup.isPending}
            onClick={() => {
              void navigate({
                to: '/workspace/member-groups/$memberGroupId',
                params: { memberGroupId },
                replace: true,
                resetScroll: true
              });
            }}
          >
            取消
          </button>
          <PendingButton
            action="confirm-delete-member-group"
            label="确认删除"
            variant="secondary"
            pending={deleteMemberGroup.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                try {
                  await deleteMemberGroup.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </section>
    </div>
  );
}

export function ManageMemberGroupMembersPage({ memberGroupId }: { readonly memberGroupId: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const selector = useMemberSelectorItemsQuery('member_group_add_member', false);
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const addMember = useMutation({
    mutationFn: (seasonMembershipId: string) =>
      service.addMemberGroupMember({ workspaceContext, memberGroupId, seasonMembershipId }),
    onSuccess: async () => {
      await invalidateMemberGroupMembershipMutationQueries(queryClient, workspaceContext, memberGroupId);
      setPickerOpen(false);
    }
  });
  const setManagerGrant = useMutation({
    mutationFn: (input: { readonly seasonMembershipId: string; readonly enabled: boolean }) =>
      service.setMemberGroupManagerGrant({
        workspaceContext,
        memberGroupId,
        seasonMembershipId: input.seasonMembershipId,
        enabled: input.enabled
      }),
    onSuccess: async () => {
      await invalidateMemberGroupManagerGrantQueries(queryClient, workspaceContext, memberGroupId);
    }
  });

  return (
    <div className="route-outlet">
      <MemberGroupMembersBackHeader
        title="管理成员"
        memberGroupId={memberGroupId}
        pending={addMember.isPending || setManagerGrant.isPending}
        action="back-to-member-group-members"
        label="返回分组成员"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId',
            params: { memberGroupId },
            search: { tab: 'members' },
            replace: true,
            resetScroll: true
          });
        }}
      />
      <MemberGroupMembersManageContent
        detail={detail}
        selector={selector}
        isPickerOpen={isPickerOpen}
        pendingSeasonMembershipId={
          addMember.isPending
            ? addMember.variables
            : setManagerGrant.isPending
              ? setManagerGrant.variables?.seasonMembershipId
              : null
        }
        actionError={actionError}
        onRetry={() => void detail.refetch()}
        onOpenRemove={(seasonMembershipId) => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/members/$seasonMembershipId/remove',
            params: { memberGroupId, seasonMembershipId },
            resetScroll: true
          });
        }}
        onTogglePicker={() => {
          setActionError(null);
          setPickerOpen(true);
        }}
        onAddMember={(seasonMembershipId) => {
          void (async () => {
            setActionError(null);
            try {
              await addMember.mutateAsync(seasonMembershipId);
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
        onToggleManager={(seasonMembershipId, enabled) => {
          void (async () => {
            setActionError(null);
            try {
              await setManagerGrant.mutateAsync({ seasonMembershipId, enabled });
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

function MemberGroupMembersManageContent({
  detail,
  selector,
  isPickerOpen,
  pendingSeasonMembershipId,
  actionError,
  onRetry,
  onOpenRemove,
  onTogglePicker,
  onAddMember,
  onToggleManager
}: {
  readonly detail: ReturnType<typeof useMemberGroupDetailQuery>;
  readonly selector: ReturnType<typeof useMemberSelectorItemsQuery>;
  readonly isPickerOpen: boolean;
  readonly pendingSeasonMembershipId: string | null | undefined;
  readonly actionError: string | null;
  readonly onRetry: () => void;
  readonly onOpenRemove: (seasonMembershipId: string) => void;
  readonly onTogglePicker: () => void;
  readonly onAddMember: (seasonMembershipId: string) => void;
  readonly onToggleManager: (seasonMembershipId: string, enabled: boolean) => void;
}): ReactNode {
  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取成员组详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError error={detail.error} retryAction="retry-manage-member-group-members" onRetry={onRetry} />
    );
  }

  const memberGroup = detail.data;
  const currentMemberIds = new Set(memberGroup.members.map((member) => member.seasonMembershipId));
  const eligibleSeasonMembershipIds = new Set(
    memberGroup.availableMembersToAdd
      .map((member) => member.seasonMembershipId)
      .filter((seasonMembershipId) => !currentMemberIds.has(seasonMembershipId))
  );

  return (
    <>
      <p className="subtle-page-subtitle">删除成员前会二次确认；添加成员入口集中在页面底部。</p>
      {memberGroup.members.length === 0 ? (
        <CompactEmptyRow body="当前成员组还没有成员。" />
      ) : (
        <section className="member-group-detail-list prototype-list" role="list">
          {memberGroup.members.map((member) => (
            <article className="prototype-list-row manage-row" role="listitem" key={member.seasonMembershipId}>
              <span className="manage-row-main">
                <span className="member-avatar" aria-hidden="true">
                  {member.nickname.trim().slice(0, 1) || '成'}
                </span>
                <span className="member-copy">
                  <strong>
                    {member.nickname}
                    {member.isGroupManager || member.isImplicitAdminManager ? (
                      <>
                        {' '}
                        <span className="icon-chip manager-shield-chip" aria-label="组管理">
                          <SvgIcon name="shield" className="icon-chip-svg" />
                        </span>
                      </>
                    ) : null}
                  </strong>
                  <small>{formatMemberGroupMemberTags(member.tagNames)}</small>
                </span>
              </span>
              {memberGroup.canManage && !member.isImplicitAdminManager ? (
                <button
                  type="button"
                  className={member.isGroupManager ? 'switch on' : 'switch'}
                  data-action="toggle-group-manager"
                  data-season-membership-id={member.seasonMembershipId}
                  role="switch"
                  aria-checked={member.isGroupManager}
                  aria-label={
                    member.isGroupManager ? `取消${member.nickname}的组管理权限` : `将${member.nickname}设为组管理`
                  }
                  disabled={pendingSeasonMembershipId === member.seasonMembershipId}
                  aria-busy={pendingSeasonMembershipId === member.seasonMembershipId ? 'true' : undefined}
                  onClick={() => onToggleManager(member.seasonMembershipId, !member.isGroupManager)}
                >
                  <span className="switch-knob" aria-hidden="true" />
                </button>
              ) : (
                <span
                  className={
                    member.isGroupManager || member.isImplicitAdminManager ? 'switch on readonly' : 'switch readonly'
                  }
                  role="switch"
                  aria-checked={member.isGroupManager || member.isImplicitAdminManager}
                  aria-label={
                    member.isGroupManager || member.isImplicitAdminManager
                      ? `${member.nickname}已是组管理`
                      : `${member.nickname}不是组管理`
                  }
                  data-season-membership-id={member.seasonMembershipId}
                >
                  <span className="switch-knob" aria-hidden="true" />
                </span>
              )}
              {memberGroup.canManage ? (
                <button
                  type="button"
                  className="delete-icon"
                  data-action="open-remove-member-group-member-confirm"
                  data-season-membership-id={member.seasonMembershipId}
                  aria-label={`从本组移出${member.nickname}`}
                  onClick={() => onOpenRemove(member.seasonMembershipId)}
                >
                  <SvgIcon name="trash" className="delete-icon-svg" />
                </button>
              ) : null}
            </article>
          ))}
        </section>
      )}
      <ActionError message={actionError} />
      {memberGroup.canAddMembers ? (
        <div className="footer-actions">
          <button type="button" className="secondary-button" data-action="add-member-to-group" onClick={onTogglePicker}>
            添加成员
          </button>
        </div>
      ) : null}
      {isPickerOpen ? (
        <InlineAddMemberPicker
          members={selector}
          eligibleSeasonMembershipIds={eligibleSeasonMembershipIds}
          pendingSeasonMembershipId={pendingSeasonMembershipId}
          onSelect={onAddMember}
        />
      ) : null}
    </>
  );
}

function InlineAddMemberPicker({
  members,
  eligibleSeasonMembershipIds,
  pendingSeasonMembershipId,
  onSelect
}: {
  readonly members: ReturnType<typeof useMemberSelectorItemsQuery>;
  readonly eligibleSeasonMembershipIds: ReadonlySet<string>;
  readonly pendingSeasonMembershipId: string | null | undefined;
  readonly onSelect: (seasonMembershipId: string) => void;
}): ReactNode {
  useEffect(() => {
    if (members.fetchStatus === 'idle' && members.status === 'pending') {
      void members.refetch();
    }
  }, [members.fetchStatus, members.refetch, members.status]);

  if (members.status === 'pending') {
    return <SectionLoading body="正在读取成员选择列表。" />;
  }

  if (members.status === 'error') {
    return (
      <RecoverableReadError
        error={members.error}
        retryAction="retry-member-group-add-member-selector"
        onRetry={() => void members.refetch()}
      />
    );
  }

  const eligibleMembers = members.data.filter((member) => eligibleSeasonMembershipIds.has(member.seasonMembershipId));

  if (eligibleMembers.length === 0) {
    return (
      <section className="inline-member-picker prototype-list">
        <CompactEmptyRow body="当前乐季没有可添加成员。" />
      </section>
    );
  }

  return (
    <section className="inline-member-picker prototype-list" role="list">
      {eligibleMembers.map((member) => (
        <button
          type="button"
          className="member-row prototype-list-row"
          data-action="select-member-to-add"
          data-season-membership-id={member.seasonMembershipId}
          disabled={pendingSeasonMembershipId === member.seasonMembershipId}
          aria-busy={pendingSeasonMembershipId === member.seasonMembershipId ? 'true' : undefined}
          role="listitem"
          key={member.seasonMembershipId}
          onClick={() => onSelect(member.seasonMembershipId)}
        >
          <span className="member-avatar" aria-hidden="true">
            {member.nickname.trim().slice(0, 1) || '成'}
          </span>
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatMemberGroupNames(member.memberGroupNames)}</small>
          </span>
        </button>
      ))}
    </section>
  );
}

export function RemoveMemberGroupMemberPage({
  memberGroupId,
  seasonMembershipId
}: {
  readonly memberGroupId: string;
  readonly seasonMembershipId: string;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [actionError, setActionError] = useState<string | null>(null);
  const removeMember = useMutation({
    mutationFn: () => service.removeMemberGroupMember({ workspaceContext, memberGroupId, seasonMembershipId }),
    onSuccess: async () => {
      await invalidateMemberGroupMembershipMutationQueries(queryClient, workspaceContext, memberGroupId);
      void navigate({
        to: '/workspace/member-groups/$memberGroupId/members',
        params: { memberGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupMembersBackHeader
          title="确认移出成员"
          memberGroupId={memberGroupId}
          pending={removeMember.isPending}
        />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupMembersBackHeader
          title="确认移出成员"
          memberGroupId={memberGroupId}
          pending={removeMember.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-confirm-remove-member-group-member"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  if (!detail.data.canManage) {
    return (
      <MemberGroupPermissionDenied
        title="确认移出成员"
        action="back-to-manage-member-group-members"
        label="返回管理成员"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/members',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  const member = detail.data.members.find((candidate) => candidate.seasonMembershipId === seasonMembershipId);
  if (!member) {
    throw new Error('Member group member not found');
  }

  return (
    <div className="route-outlet">
      <MemberGroupMembersBackHeader
        title="确认移出成员"
        memberGroupId={memberGroupId}
        pending={removeMember.isPending}
      />
      <section className="confirm-card">
        <h2>{`移出${member.nickname}？`}</h2>
        <p>{`移出后，${member.nickname}会失去本组标签和本组组管理权限。成员身份和其他分组不受影响。`}</p>
        <ActionError message={actionError} />
        <div className="footer-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="cancel-remove-member-group-member"
            disabled={removeMember.isPending}
            onClick={() => {
              void navigate({
                to: '/workspace/member-groups/$memberGroupId/members',
                params: { memberGroupId },
                replace: true,
                resetScroll: true
              });
            }}
          >
            取消
          </button>
          <PendingButton
            action="confirm-remove-member-group-member"
            label="确认移出"
            variant="secondary"
            pending={removeMember.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                try {
                  await removeMember.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </section>
    </div>
  );
}

export function ManageMemberGroupTagsPage({ memberGroupId }: { readonly memberGroupId: string }): ReactNode {
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const backToTagsStatus = (): void => {
    void navigate({
      to: '/workspace/member-groups/$memberGroupId',
      params: { memberGroupId },
      search: { tab: 'tags' },
      replace: true,
      resetScroll: true
    });
  };

  if (detail.status === 'success' && !detail.data.canManage) {
    return (
      <MemberGroupPermissionDenied
        title="管理标签"
        action="back-to-member-group-tags"
        label="返回组内标签"
        onBack={backToTagsStatus}
      />
    );
  }

  return (
    <div className="route-outlet">
      <MemberGroupTagsBackHeader
        title="管理标签"
        memberGroupId={memberGroupId}
        action="back-to-member-group-tags"
        label="返回组内标签"
        onBack={backToTagsStatus}
      />
      <MemberGroupTagsManageContent
        detail={detail}
        onRetry={() => void detail.refetch()}
        onOpenCreate={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags/new',
            params: { memberGroupId },
            resetScroll: true
          });
        }}
        onOpenDetail={(memberGroupTagId) => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags/$memberGroupTagId',
            params: { memberGroupId, memberGroupTagId },
            resetScroll: true
          });
        }}
        onOpenRename={(memberGroupTagId) => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags/$memberGroupTagId/rename',
            params: { memberGroupId, memberGroupTagId },
            resetScroll: true
          });
        }}
        onOpenDelete={(memberGroupTagId) => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags/$memberGroupTagId/delete',
            params: { memberGroupId, memberGroupTagId },
            resetScroll: true
          });
        }}
      />
    </div>
  );
}

function MemberGroupTagsManageContent({
  detail,
  onRetry,
  onOpenCreate,
  onOpenDetail,
  onOpenRename,
  onOpenDelete
}: {
  readonly detail: ReturnType<typeof useMemberGroupDetailQuery>;
  readonly onRetry: () => void;
  readonly onOpenCreate: () => void;
  readonly onOpenDetail: (memberGroupTagId: string) => void;
  readonly onOpenRename: (memberGroupTagId: string) => void;
  readonly onOpenDelete: (memberGroupTagId: string) => void;
}): ReactNode {
  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取成员组详情。" />;
  }

  if (detail.status === 'error') {
    return <RecoverableReadError error={detail.error} retryAction="retry-manage-member-group-tags" onRetry={onRetry} />;
  }

  return (
    <>
      <p className="subtle-page-subtitle">标签只做展示和筛选。删除标签前会二次确认。</p>
      {detail.data.tags.length === 0 ? (
        <CompactEmptyRow body="当前成员组还没有标签。" />
      ) : (
        <section className="member-group-detail-list prototype-list" role="list">
          {detail.data.tags.map((tag) => (
            <article className="prototype-list-row manage-row" role="listitem" key={tag.memberGroupTagId}>
              <button
                type="button"
                className="manage-row-main"
                data-action="open-member-group-tag"
                data-member-group-tag-id={tag.memberGroupTagId}
                aria-label={`管理标签${tag.name}`}
                onClick={() => onOpenDetail(tag.memberGroupTagId)}
              >
                <span className="member-copy">
                  <strong>{tag.name}</strong>
                  <small>{`${tag.memberCount} 名成员 · 点击查看成员标记`}</small>
                </span>
                <span className="row-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
              {detail.data.canManage ? (
                <>
                  <button
                    type="button"
                    className="edit-icon"
                    data-action="open-rename-member-group-tag"
                    data-member-group-tag-id={tag.memberGroupTagId}
                    aria-label={`重命名标签${tag.name}`}
                    onClick={() => onOpenRename(tag.memberGroupTagId)}
                  >
                    <SvgIcon name="edit" className="edit-icon-svg" />
                  </button>
                  <button
                    type="button"
                    className="delete-icon"
                    data-action="open-delete-member-group-tag-confirm"
                    data-member-group-tag-id={tag.memberGroupTagId}
                    aria-label={`删除标签${tag.name}`}
                    onClick={() => onOpenDelete(tag.memberGroupTagId)}
                  >
                    <SvgIcon name="trash" className="delete-icon-svg" />
                  </button>
                </>
              ) : null}
            </article>
          ))}
        </section>
      )}
      {detail.data.canManage ? (
        <div className="footer-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="open-create-member-group-tag"
            onClick={onOpenCreate}
          >
            新增标签
          </button>
        </div>
      ) : null}
    </>
  );
}

export function CreateMemberGroupTagPage({ memberGroupId }: { readonly memberGroupId: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const createTag = useMutation({
    mutationFn: (name: string) => service.createMemberGroupTag({ workspaceContext, memberGroupId, name }),
    onSuccess: async () => {
      await invalidateMemberGroupTagMutationQueries(queryClient, workspaceContext, memberGroupId);
      flushSync(() => setSubmitNavigationAllowed(true));
      void navigate({
        to: '/workspace/member-groups/$memberGroupId',
        params: { memberGroupId },
        search: { tab: 'tags' },
        replace: true,
        resetScroll: true
      });
    }
  });

  return (
    <MemberGroupTagFormRoute
      title="新增标签"
      memberGroupId={memberGroupId}
      detail={detail}
      initialName=""
      action="create-member-group-tag"
      label="创建标签"
      pending={createTag.isPending}
      isSubmitNavigationAllowed={isSubmitNavigationAllowed}
      onSubmit={async (name) => {
        try {
          await createTag.mutateAsync(name);
        } catch (error) {
          setSubmitNavigationAllowed(false);
          throw error;
        }
      }}
    />
  );
}

export function RenameMemberGroupTagPage({
  memberGroupId,
  memberGroupTagId
}: {
  readonly memberGroupId: string;
  readonly memberGroupTagId: string;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const renameTag = useMutation({
    mutationFn: (name: string) =>
      service.renameMemberGroupTag({ workspaceContext, memberGroupId, memberGroupTagId, name }),
    onSuccess: async () => {
      await invalidateMemberGroupTagMutationQueries(queryClient, workspaceContext, memberGroupId);
      flushSync(() => setSubmitNavigationAllowed(true));
      void navigate({
        to: '/workspace/member-groups/$memberGroupId/tags',
        params: { memberGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  const tag =
    detail.status === 'success'
      ? detail.data.tags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId)
      : null;
  if (detail.status === 'success' && !tag) {
    throw new Error('Member group tag not found');
  }

  return (
    <MemberGroupTagFormRoute
      key={tag?.memberGroupTagId}
      title="重命名标签"
      memberGroupId={memberGroupId}
      detail={detail}
      initialName={tag?.name ?? ''}
      action="rename-member-group-tag"
      label="保存名称"
      pending={renameTag.isPending}
      isSubmitNavigationAllowed={isSubmitNavigationAllowed}
      onSubmit={async (name) => {
        try {
          await renameTag.mutateAsync(name);
        } catch (error) {
          setSubmitNavigationAllowed(false);
          throw error;
        }
      }}
    />
  );
}

function MemberGroupTagFormRoute({
  title,
  memberGroupId,
  detail,
  initialName,
  action,
  label,
  pending,
  isSubmitNavigationAllowed,
  onSubmit
}: {
  readonly title: string;
  readonly memberGroupId: string;
  readonly detail: ReturnType<typeof useMemberGroupDetailQuery>;
  readonly initialName: string;
  readonly action: string;
  readonly label: string;
  readonly pending: boolean;
  readonly isSubmitNavigationAllowed: boolean;
  readonly onSubmit: (name: string) => Promise<void>;
}): ReactNode {
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isDiscardNavigationAllowed, setDiscardNavigationAllowed] = useState(false);
  const isDirty = name !== initialName;
  const leave = (): void => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    void navigate({
      to: '/workspace/member-groups/$memberGroupId/tags',
      params: { memberGroupId },
      replace: true,
      resetScroll: true
    });
  };

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader title={title} memberGroupId={memberGroupId} pending={pending} onBack={leave} />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader title={title} memberGroupId={memberGroupId} pending={pending} onBack={leave} />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-member-group-tag-form"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  if (!detail.data.canManage) {
    return (
      <MemberGroupPermissionDenied
        title={title}
        action="back-to-manage-member-group-tags"
        label="返回管理标签"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <MemberGroupTagsBackHeader title={title} memberGroupId={memberGroupId} pending={pending} onBack={leave} />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          body="离开后，当前标签更改不会保存。"
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={() => {
            setShowDiscardDialog(false);
            setDiscardNavigationAllowed(true);
            void navigate({
              to: '/workspace/member-groups/$memberGroupId/tags',
              params: { memberGroupId },
              replace: true,
              resetScroll: true
            });
          }}
        />
      ) : null}
      <MemberGroupTagFormLeaveGuard
        memberGroupId={memberGroupId}
        isDirty={isDirty}
        isDisabled={isSubmitNavigationAllowed || isDiscardNavigationAllowed}
      />
      <section className="create-member-group-form">
        <p className="compact-note">标签仅用于展示和筛选。</p>
        <TextField
          label="标签名称"
          field="member-group-tag-name"
          value={name}
          onChange={(nextName) => {
            setActionError(null);
            setName(nextName);
          }}
        />
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton
          action={action}
          label={label}
          variant="primary"
          pending={pending}
          onClick={() => {
            void (async () => {
              setActionError(null);
              try {
                await onSubmit(name);
              } catch (error) {
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
      </div>
    </div>
  );
}

export function DeleteMemberGroupTagPage({
  memberGroupId,
  memberGroupTagId
}: {
  readonly memberGroupId: string;
  readonly memberGroupTagId: string;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [actionError, setActionError] = useState<string | null>(null);
  const deleteTag = useMutation({
    mutationFn: () => service.deleteMemberGroupTag({ workspaceContext, memberGroupId, memberGroupTagId }),
    onSuccess: async () => {
      await invalidateMemberGroupTagMutationQueries(queryClient, workspaceContext, memberGroupId);
      void navigate({
        to: '/workspace/member-groups/$memberGroupId/tags',
        params: { memberGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader title="确认删除标签" memberGroupId={memberGroupId} pending={deleteTag.isPending} />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader title="确认删除标签" memberGroupId={memberGroupId} pending={deleteTag.isPending} />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-confirm-delete-member-group-tag"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  if (!detail.data.canManage) {
    return (
      <MemberGroupPermissionDenied
        title="确认删除标签"
        action="back-to-manage-member-group-tags"
        label="返回管理标签"
        onBack={() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  const tag = detail.data.tags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId);
  if (!tag) {
    throw new Error('Member group tag not found');
  }

  return (
    <div className="route-outlet">
      <MemberGroupTagsBackHeader title="确认删除标签" memberGroupId={memberGroupId} pending={deleteTag.isPending} />
      <section className="confirm-card">
        <h2>{`删除“${tag.name}”？`}</h2>
        <p>删除后，本组成员身上的该标签会被移除。标签不影响权限。</p>
        <ActionError message={actionError} />
        <div className="footer-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="cancel-delete-member-group-tag"
            disabled={deleteTag.isPending}
            onClick={() => {
              void navigate({
                to: '/workspace/member-groups/$memberGroupId/tags',
                params: { memberGroupId },
                replace: true,
                resetScroll: true
              });
            }}
          >
            取消
          </button>
          <PendingButton
            action="confirm-delete-member-group-tag"
            label="确认删除"
            variant="secondary"
            pending={deleteTag.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                try {
                  await deleteTag.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </section>
    </div>
  );
}

export function MemberGroupTagDetailPage({
  memberGroupId,
  memberGroupTagId
}: {
  readonly memberGroupId: string;
  readonly memberGroupTagId: string;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const detail = useMemberGroupDetailQuery(memberGroupId);
  const [actionError, setActionError] = useState<string | null>(null);
  const toggleMembership = useMutation({
    mutationFn: (input: { readonly seasonMembershipId: string; readonly enabled: boolean }) =>
      service.setMemberGroupTagMembership({
        workspaceContext,
        memberGroupId,
        memberGroupTagId,
        seasonMembershipId: input.seasonMembershipId,
        enabled: input.enabled
      }),
    onSuccess: async () => {
      await invalidateMemberGroupTagMutationQueries(queryClient, workspaceContext, memberGroupId);
    }
  });

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader
          title="标签详情"
          memberGroupId={memberGroupId}
          pending={toggleMembership.isPending}
        />
        <SectionLoading body="正在读取成员组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <MemberGroupTagsBackHeader
          title="标签详情"
          memberGroupId={memberGroupId}
          pending={toggleMembership.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-member-group-tag-detail"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const tag = detail.data.tags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId);
  if (!tag) {
    throw new Error('Member group tag not found');
  }

  return (
    <div className="route-outlet">
      <MemberGroupTagsBackHeader title={tag.name} memberGroupId={memberGroupId} pending={toggleMembership.isPending} />
      <p className="compact-note">标签不产生权限。</p>
      <ActionError message={actionError} />
      <section className="member-group-member-list member-group-detail-list prototype-list" role="list">
        {detail.data.members.map((member) => {
          const hasTag = member.tagIds.includes(memberGroupTagId);
          const isPending =
            toggleMembership.isPending && toggleMembership.variables?.seasonMembershipId === member.seasonMembershipId;
          return (
            <article className="member-row prototype-list-row" role="listitem" key={member.seasonMembershipId}>
              <span className="member-avatar" aria-hidden="true">
                {member.nickname.trim().slice(0, 1) || '成'}
              </span>
              <span className="member-copy">
                <strong>{member.nickname}</strong>
              </span>
              {detail.data.canManage ? (
                <button
                  type="button"
                  className={hasTag ? 'switch on' : 'switch'}
                  data-action="toggle-member-group-tag-membership"
                  data-member-group-tag-id={memberGroupTagId}
                  data-season-membership-id={member.seasonMembershipId}
                  role="switch"
                  aria-checked={hasTag}
                  aria-label={
                    hasTag ? `移除${member.nickname}的标签${tag.name}` : `为${member.nickname}添加标签${tag.name}`
                  }
                  disabled={isPending}
                  aria-busy={isPending ? 'true' : undefined}
                  onClick={() => {
                    void (async () => {
                      setActionError(null);
                      try {
                        await toggleMembership.mutateAsync({
                          seasonMembershipId: member.seasonMembershipId,
                          enabled: !hasTag
                        });
                      } catch (error) {
                        setActionError(formatActionError(error));
                      }
                    })();
                  }}
                >
                  <span className="switch-knob" aria-hidden="true" />
                </button>
              ) : (
                <span
                  className={hasTag ? 'switch on readonly' : 'switch readonly'}
                  data-member-group-tag-id={memberGroupTagId}
                  data-season-membership-id={member.seasonMembershipId}
                  role="switch"
                  aria-checked={hasTag}
                  aria-label={
                    hasTag ? `${member.nickname}已有标签${tag.name}` : `${member.nickname}没有标签${tag.name}`
                  }
                >
                  <span className="switch-knob" aria-hidden="true" />
                </span>
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
}

function MemberGroupDetailBackHeader({
  title,
  memberGroupId,
  pending = false,
  onBack
}: {
  readonly title: string;
  readonly memberGroupId: string;
  readonly pending?: boolean;
  readonly onBack?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action="back-to-member-group-detail"
      label="返回成员组详情"
      pending={pending}
      onBack={
        onBack ??
        (() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        })
      }
    />
  );
}

function MemberGroupMembersBackHeader({
  title,
  memberGroupId,
  pending = false,
  action = 'back-to-manage-member-group-members',
  label = '返回管理成员',
  onBack
}: {
  readonly title: string;
  readonly memberGroupId: string;
  readonly pending?: boolean;
  readonly action?: string;
  readonly label?: string;
  readonly onBack?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action={action}
      label={label}
      pending={pending}
      onBack={
        onBack ??
        (() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/members',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        })
      }
    />
  );
}

function MemberGroupTagsBackHeader({
  title,
  memberGroupId,
  pending = false,
  action = 'back-to-manage-member-group-tags',
  label = '返回管理标签',
  onBack
}: {
  readonly title: string;
  readonly memberGroupId: string;
  readonly pending?: boolean;
  readonly action?: string;
  readonly label?: string;
  readonly onBack?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action={action}
      label={label}
      pending={pending}
      onBack={
        onBack ??
        (() => {
          void navigate({
            to: '/workspace/member-groups/$memberGroupId/tags',
            params: { memberGroupId },
            replace: true,
            resetScroll: true
          });
        })
      }
    />
  );
}

function MemberGroupPermissionDenied({
  title,
  body = '无权管理这个成员组。',
  action,
  label,
  onBack
}: {
  readonly title: string;
  readonly body?: string;
  readonly action: string;
  readonly label: string;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="route-outlet">
      <BackHeader title={title} action={action} label={label} onBack={onBack} />
      <section className="panel" role="alert" tabIndex={-1}>
        <p>{body}</p>
      </section>
    </div>
  );
}

function useMemberGroupCreateLeaveBlocker({
  isDirty,
  isDisabled
}: {
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}) {
  return useBlocker({
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isDisabled,
    disabled: !isDirty || isDisabled,
    shouldBlockFn: ({ current, next }) => {
      if (!isMemberGroupCreateFlowPath(current.pathname)) {
        return false;
      }

      return !isMemberGroupCreateFlowPath(next.pathname);
    }
  });
}

function useMemberGroupRenameLeaveBlocker({
  memberGroupId,
  isDirty,
  isDisabled
}: {
  readonly memberGroupId: string;
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}) {
  return useBlocker({
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isDisabled,
    disabled: !isDirty || isDisabled,
    shouldBlockFn: ({ current, next }) => {
      const renamePath = `/workspace/member-groups/${memberGroupId}/rename`;
      if (current.pathname !== renamePath) {
        return false;
      }

      return next.pathname !== renamePath;
    }
  });
}

function useMemberGroupTagFormLeaveBlocker({
  memberGroupId,
  isDirty,
  isDisabled
}: {
  readonly memberGroupId: string;
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}) {
  return useBlocker({
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isDisabled,
    disabled: !isDirty || isDisabled,
    shouldBlockFn: ({ current, next }) => {
      if (!isMemberGroupTagFormPath(current.pathname, memberGroupId)) {
        return false;
      }

      return !isMemberGroupTagFormPath(next.pathname, memberGroupId);
    }
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isMemberGroupCreateFlowPath(pathname: string): boolean {
  return /^\/workspace\/member-groups\/new(?:\/initial-member)?$/.test(pathname);
}

function isMemberGroupTagFormPath(pathname: string, memberGroupId: string): boolean {
  const escapedMemberGroupId = escapeRegExp(memberGroupId);
  return new RegExp(`^/workspace/member-groups/${escapedMemberGroupId}/tags/(?:new|[^/]+/rename)$`).test(pathname);
}

function formatMemberGroupSummaryMeta(memberGroup: MemberGroupSummary): string {
  return [
    memberGroup.isDefault ? '默认分组' : '自定义分组',
    `${memberGroup.memberCount} 人`,
    memberGroup.canManage ? '可管理' : '仅查看'
  ].join(' · ');
}

function formatMemberRole(role: MemberGroupMemberListItem['role']): string {
  return role === 'admin' ? '管理员' : '成员';
}

function formatMemberGroupMemberTags(tagNames: readonly string[]): string {
  return tagNames.length === 0 ? '暂无标签' : tagNames.join(' · ');
}

function formatMemberGroupPermissionStatus(member: MemberGroupMemberListItem): string {
  if (member.isImplicitAdminManager) {
    return '合唱团管理员自动拥有管理权限';
  }
  if (member.isGroupManager) {
    return '组管理员';
  }
  return '普通成员';
}

function canCreateMemberGroups(appContext: WorkspaceBootstrapContext): boolean {
  return Boolean(appContext.currentMembership && appContext.currentSeason);
}

function canManagePermissionGroups(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.isCurrentUserAdmin && appContext.currentMembership.role === 'admin';
}

function canDeleteMemberGroups(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.isCurrentUserAdmin && appContext.currentMembership.role === 'admin';
}

function canMutateMemberGroup(memberGroup: MemberGroupDetail): boolean {
  return memberGroup.canManage && !memberGroup.isDefault;
}

function canRenameMemberGroup(memberGroup: Pick<MemberGroupSummary, 'canManage' | 'isDefault'>): boolean {
  return memberGroup.canManage && !memberGroup.isDefault;
}

function canDeleteMemberGroup(
  canDeleteGroups: boolean,
  memberGroup: Pick<MemberGroupSummary, 'isDefault' | 'memberCount'>
): boolean {
  return canDeleteGroups && !memberGroup.isDefault && memberGroup.memberCount === 0;
}

function getMemberGroupDeleteBlockReason(
  appContext: WorkspaceBootstrapContext,
  memberGroup: MemberGroupDetail
): string | null {
  if (!memberGroup.canManage) {
    return '无权管理这个成员组。';
  }
  if (!canDeleteMemberGroups(appContext)) {
    return '无权删除这个成员组。';
  }
  if (memberGroup.isDefault) {
    return '默认成员组不能删除。';
  }
  if (memberGroup.memberCount > 0) {
    return '请先移出成员后再删除这个成员组。';
  }
  return null;
}

function isMemberGroupCreateDraftDirty(
  draft: Pick<MemberGroupCreateDraftStoreValue, 'name' | 'initialMember'>
): boolean {
  return draft.name !== '' || draft.initialMember !== null;
}

function formatPermissionGroupSummaryMeta(permissionGroup: PermissionGroupSummary): string {
  return `${formatPermissionKeys(permissionGroup.permissionKeys)} · ${permissionGroup.memberCount} 人`;
}

function formatPermissionKeys(permissionKeys: PermissionGroupSummary['permissionKeys']): string {
  return formatPermissionKeyLabels(permissionKeys);
}
